/**
 * Deferred conditional grants — the wait-and-see window for hero conditions that
 * cannot be satisfied at play time but may become true later this turn
 * (WP-568 / D-24377; extended WP-656 / D-24467).
 *
 * OPERATOR DECISION: a condition of the form "If you [did X] this turn" is a
 * WHOLE-TURN window, not a snapshot taken when the card is played. Thor's Surge
 * of Power therefore grants its +3 attack retroactively once the turn's recruit
 * total reaches 8, even if that recruit arrives after the card is in play.
 *
 * TWO SHAPES LIVE HERE:
 *
 *  1. NUMERIC-THRESHOLD, ONE-SHOT (WP-568). `recruitMadeThisTurnAtLeast` /
 *     `distinctHeroClassesAtLeast`: a crossed threshold STAYS crossed, so the
 *     grant is removed as it fires (`:134`) and grants EXACTLY ONCE — a threshold
 *     crossed, dropped and re-crossed within one turn still grants once.
 *     `heroClassMatch` and `requiresTeam` are deliberately OUT of scope (they keep
 *     on-play evaluation); the boundary is "is it a numeric threshold", not "does
 *     it read inPlay" — `distinctHeroClassesAtLeast` (Spectrum) reads hand + play
 *     (D-24694) and is in scope, so a Hero of a new class drawn later this turn
 *     can still cross it.
 *     Converting the class gates would change every `[hc:X]` card and remove
 *     play-ordering skill from class synergy. Sticky per-turn EVENT predicates
 *     (`masterStrikePlayedThisTurn` / `masterStrikeOrAmbushPlayedThisTurn`, WP-743 /
 *     D-24566) are shape #1 too: once the event happens it stays true for the turn.
 *
 *  2. EDGE-TRIGGERED, REPEATABLE-PER-EVENT (WP-656 / D-24467).
 *     `defeatedVillainOrMastermindThisTurn` (Diamond Form: "Whenever you defeat a
 *     Villain or Mastermind this turn, you get +3 Recruit") is NOT a threshold: it
 *     must grant +3 PER qualifying defeat, so a boolean "defeated this turn" the
 *     resolver re-reads would over-fire on every subsequent non-defeat move (the
 *     onMove resolution runs after EVERY move, not just fights). It is modelled as
 *     an EDGE: the fight sites set G.villainOrMastermindDefeatedSinceResolve
 *     (gated on a pending grant), the condition reads it, and
 *     resolveDeferredHeroGrants CONSUMES it after each resolution — so the gate is
 *     true only on the move right after a defeat. The one-shot removal above would
 *     kill the grant after the first defeat, so resolveDeferredHeroGrants RE-ARMS a
 *     fired defeat grant (re-records it via the documented "a fire callback may
 *     defer a new grant" support below) so it survives to credit the next defeat.
 *     The turn boundary drops it like any other entry. The `:134` one-shot removal
 *     is UNCHANGED — repeatability comes from the caller re-arming, not from this
 *     helper retaining entries.
 *
 * Pure: these helpers read and mutate `G` only through the entry list; the
 * firing itself is delegated by callback so this module never imports the hero
 * effect executor (which would be a cycle). No boardgame.io import.
 */
import { BYSTANDER_EXT_ID, WOUND_EXT_ID } from '../setup/pilesInit.js';
import { MASTER_STRIKE_THIS_TURN_CONDITION_TYPE, MASTER_STRIKE_OR_AMBUSH_THIS_TURN_CONDITION_TYPE, OWNED_CARD_KOED_CONDITION_TYPE, OWNED_HERO_KOED_CONDITION_TYPE, DECK_CARD_KOED_CONDITION_TYPE, ATTACK_ICON_DECK_KO_CONDITION_TYPE, NON_GREY_HERO_DECK_KO_CONDITION_TYPE, matchReadsConditionType, } from './heroConditions.evaluate.js';
// why: WP-656 / D-24467 — the single EDGE-TRIGGERED wait-and-see condition type.
// Its grant re-arms per defeat rather than firing once (see shape #2 above), which
// resolveDeferredHeroGrants keys on to re-record a fired grant. Kept as a named
// const so the array below, the evaluator case, the re-arm check, and the setup
// marker branch all reference one literal (no cross-file drift).
export const REPEATABLE_DEFEAT_CONDITION_TYPE = 'defeatedVillainOrMastermindThisTurn';
// why: WP-568 / D-24377 §1 (extended WP-656 / D-24467) — the closed set of
// condition types that get the whole-turn wait-and-see window. A type NOT listed
// here keeps on-play evaluation, which is why adding one is a deliberate act rather
// than an emergent behaviour change. Every entry here MUST have an evaluateCondition
// case (heroConditions.evaluate.ts) — a listed type with no case silently never
// fires (the `default → false` path). The runtime drift pin in
// deferredConditionalGrants.test.ts enforces that lockstep.
export const WAIT_AND_SEE_CONDITION_TYPES = [
    'recruitMadeThisTurnAtLeast',
    'distinctHeroClassesAtLeast',
    REPEATABLE_DEFEAT_CONDITION_TYPE,
    // why: WP-665 / D-24476 — "if you drew two cards this turn" (Gamma-Draining
    // Nanites' Transform gate) is a whole-turn numeric threshold: the transform
    // hook waits and re-fires once the effect-draw count reaches N later this turn.
    // Has an evaluateCondition case (heroConditions.evaluate.ts) — the lockstep the
    // drift pin below enforces.
    'cardsDrawnThisTurnAtLeast',
    // why: WP-777 / D-24616 — "If you discarded any cards this turn" (New Wings, Pumpkin
    // Bombs) is a whole-turn numeric threshold: a card played before the discard waits and
    // grants once when a later card-effect hand discard this turn reaches N. Has an
    // evaluateCondition case (heroConditions.evaluate.ts) — the lockstep the drift pin
    // below enforces.
    'cardsDiscardedThisTurnAtLeast',
    // why: WP-743 / D-24566 — "a Master Strike (or an Ambush Villain) was played this
    // turn" (Grief / Spring the Trap) is a STICKY per-turn predicate — once true it stays
    // true for the turn — so it behaves as a count >= 1 threshold: shape #1, one-shot, no
    // re-arm. Extends D-24377's numeric-threshold boundary to sticky event predicates.
    // Both have evaluateCondition cases (the lockstep the drift pin enforces).
    MASTER_STRIKE_THIS_TURN_CONDITION_TYPE,
    MASTER_STRIKE_OR_AMBUSH_THIS_TURN_CONDITION_TYPE,
    // why: WP-853 / D-24789 — "Whenever a card you own is KO'd this turn" (Absorb
    // Energies) is shape #2 with a per-owner COUNT: the grant fires once per counted KO and
    // re-arms. Has an evaluateCondition case (the lockstep the drift pin enforces).
    OWNED_CARD_KOED_CONDITION_TYPE,
    // why: WP-856 / D-24792 — "The first time that one of your Heroes [or a Hero from your
    // deck or discard pile] is KO'd this turn" (Possessed by Brother's Spirit) reads the
    // per-owner Hero-KO count but is shape #1: it fires once and is NOT re-armed, which is
    // what "the first time" means. Has an evaluateCondition case (the lockstep pin).
    OWNED_HERO_KOED_CONDITION_TYPE,
    // why: WP-855 / D-24791 — "Whenever a card is KO'd from your deck this turn" (Endless
    // Appetite) is shape #2 with a per-owner DECK count: fires once per counted KO and re-arms,
    // like the owned-KO entry. Has an evaluateCondition case (the lockstep the drift pin enforces).
    DECK_CARD_KOED_CONDITION_TYPE,
    // why: WP-859 / D-24795 — "Then, if a card with an [icon:attack] icon / a non-grey Hero was
    // KO'd from your deck this turn, Transform" (Metamorphosis, Bursting with Life) reads the
    // sticky turn record, so it is shape #1: fire once, not re-armed. RS-1 — the whole-turn
    // window (D-24476 parity with Gamma-Draining Nanites): the Transform fires the first time the
    // condition holds this turn. Both have evaluateCondition cases (the lockstep the pin enforces).
    ATTACK_ICON_DECK_KO_CONDITION_TYPE,
    NON_GREY_HERO_DECK_KO_CONDITION_TYPE,
];
// why: WP-859 / D-24795 — the one count source that reads the deck-KO turn record (World-Spanning
// Hunger). Typed against the HeroCountSource union, so a renamed source fails to compile.
const NON_GREY_HEROES_KOED_FROM_DECK_COUNT_SOURCE = 'non-grey-heroes-koed-from-deck-this-turn';
/**
 * Reports whether a failed condition earns the whole-turn wait-and-see window.
 *
 * @param condition - The condition that failed at play time.
 * @returns True when the condition is one of the two numeric thresholds.
 */
export function isWaitAndSeeCondition(condition) {
    return WAIT_AND_SEE_CONDITION_TYPES.includes(condition.type);
}
/**
 * Records a hook whose numeric-threshold gate failed, to be re-checked this turn.
 *
 * why: the container is materialized LAZILY — a game in which nothing ever defers
 * carries no new `G` field at all, which is what keeps both sentinel oracles
 * byte-unchanged (D-24377 §6). It is deliberately NOT named `pending*`: every
 * `G.pending*Choices[]` in this engine is an INTERACTIVE choice with a resolve
 * move, and a parked one lacking a `UIState` projection and prompt hard-freezes
 * the human player. A deferred grant takes no player input and needs no prompt.
 *
 * @param G - The game state, mutated in place.
 * @param playerId - The player whose card is waiting.
 * @param cardId - The played card whose ability is waiting.
 * @param hookIndex - Index into `G.heroAbilityHooks` (built at setup, never
 *   mutated at runtime, so the index is stable for the match).
 */
export function recordDeferredConditionalGrant(G, playerId, cardId, hookIndex) {
    if (G.deferredConditionalGrants === undefined) {
        G.deferredConditionalGrants = [];
    }
    G.deferredConditionalGrants.push({ playerId, cardId, hookIndex });
}
/**
 * Counts one KO of a card the given player owns, for a waiting owned-KO or Hero-KO grant.
 *
 * why: WP-853 / D-24789 — called by every KO site that takes a card from a player's
 * hand, deck, discard pile or cards in play, once per KO'd card. GATED: each count is
 * written only while a waiting grant's hook reads its condition type, so a game with no
 * waiting Absorb Energies / Possessed by Brother's Spirit never gains either key, and the
 * resolve deletes both after every move, so they never reach a hash. Keyed PER OWNER
 * because a Villain or Mastermind can KO another player's cards, and only the owner's
 * grant may fire.
 * why: WP-856 / D-24792 — the KO'd card's id lets the Hero count skip Wounds and
 * Bystanders (the WP-828 isHeroCard rule: anything else in a player's zones is a Hero).
 *
 * why: WP-855 / D-24791 — `fromZone` is the zone the card left, required at every owned site
 * so the compiler makes a new site state it; a KO from the deck also counts for a waiting
 * "KO'd from your deck" grant.
 *
 * @param G - The game state, mutated in place.
 * @param ownerPlayerID - The player whose zone the KO'd card left.
 * @param cardId - The card that was KO'd.
 * @param fromZone - The owner's zone the card left.
 */
export function recordOwnedCardKoed(G, ownerPlayerID, cardId, fromZone) {
    if (hasWaitingGrantWithCondition(G, OWNED_CARD_KOED_CONDITION_TYPE)) {
        if (G.ownedCardsKoedSinceResolve === undefined) {
            G.ownedCardsKoedSinceResolve = {};
        }
        const previousCount = G.ownedCardsKoedSinceResolve[ownerPlayerID] ?? 0;
        G.ownedCardsKoedSinceResolve[ownerPlayerID] = previousCount + 1;
    }
    const isHero = cardId !== WOUND_EXT_ID && cardId !== BYSTANDER_EXT_ID;
    if (isHero && hasWaitingGrantWithCondition(G, OWNED_HERO_KOED_CONDITION_TYPE)) {
        if (G.ownedHeroesKoedSinceResolve === undefined) {
            G.ownedHeroesKoedSinceResolve = {};
        }
        const previousHeroCount = G.ownedHeroesKoedSinceResolve[ownerPlayerID] ?? 0;
        G.ownedHeroesKoedSinceResolve[ownerPlayerID] = previousHeroCount + 1;
    }
    // why: WP-855 / D-24791 — the deck count, its own gate: written only while a waiting grant
    // reads `deckCardKoedThisTurn`, so a game with no waiting Endless Appetite never gains the
    // key and no hash moves. Keyed per owner like the counts above (a Villain can KO from
    // another player's deck, and only the owner's grant may fire).
    if (fromZone === 'deck' && hasWaitingGrantWithCondition(G, DECK_CARD_KOED_CONDITION_TYPE)) {
        if (G.deckCardsKoedSinceResolve === undefined) {
            G.deckCardsKoedSinceResolve = {};
        }
        const previousDeckCount = G.deckCardsKoedSinceResolve[ownerPlayerID] ?? 0;
        G.deckCardsKoedSinceResolve[ownerPlayerID] = previousDeckCount + 1;
    }
    // why: WP-859 / D-24795 — the deck-KO turn record ("KO'd from your deck this turn"). TURN-scoped
    // because a KO before the reader is played counts, so it cannot wait for a deferred grant like
    // the counts above; it is gated instead on a reader hook, so a game without Metamorphosis,
    // Bursting with Life or World-Spanning Hunger never gains the key and no hash moves. The zone
    // test runs first, so the hook scan runs only at deck sites. Keyed per owner, in KO order.
    if (fromZone === 'deck' && hasDeckKoRecordReader(G)) {
        if (G.cardsKoedFromDeckThisTurn === undefined) {
            G.cardsKoedFromDeckThisTurn = {};
        }
        const ownerRecord = G.cardsKoedFromDeckThisTurn[ownerPlayerID];
        if (ownerRecord === undefined) {
            G.cardsKoedFromDeckThisTurn[ownerPlayerID] = [cardId];
        }
        else {
            ownerRecord.push(cardId);
        }
    }
}
/**
 * Reports whether any hook in this match reads the deck-KO turn record.
 *
 * why: WP-859 / D-24795 — not the waiting-grant gate the counts use: a KO made before the reader
 * card is played must be in the record, and at that moment nothing waits. Hooks are built once at
 * setup, so the answer is constant for the match (the D-24616 posture). The condition half reuses
 * matchReadsConditionType; the count half scans the effects for World-Spanning Hunger's source.
 *
 * @param G - The game state (read-only).
 * @returns True when a hook carries either deck-KO condition or the deck-KO count source.
 */
function hasDeckKoRecordReader(G) {
    if (matchReadsConditionType(G, ATTACK_ICON_DECK_KO_CONDITION_TYPE)) {
        return true;
    }
    if (matchReadsConditionType(G, NON_GREY_HERO_DECK_KO_CONDITION_TYPE)) {
        return true;
    }
    if (!Array.isArray(G.heroAbilityHooks)) {
        return false;
    }
    for (const hook of G.heroAbilityHooks) {
        for (const effect of hook.effects ?? []) {
            if (effect.countSource === NON_GREY_HEROES_KOED_FROM_DECK_COUNT_SOURCE) {
                return true;
            }
        }
    }
    return false;
}
/**
 * Reports whether any waiting deferred grant's hook reads the given condition type.
 *
 * @param G - The game state (read-only).
 * @param conditionType - The condition type to look for.
 * @returns True when a grant reading that condition is waiting.
 */
function hasWaitingGrantWithCondition(G, conditionType) {
    const entries = G.deferredConditionalGrants;
    if (entries === undefined || !G.heroAbilityHooks) {
        return false;
    }
    for (const entry of entries) {
        const hook = G.heroAbilityHooks[entry.hookIndex];
        if (hook === undefined || hook.conditions === undefined) {
            continue;
        }
        for (const condition of hook.conditions) {
            if (condition.type === conditionType) {
                return true;
            }
        }
    }
    return false;
}
/**
 * Drops every deferred grant. Called at the turn boundary.
 *
 * why: the window is THIS turn. A threshold never reached during the turn never
 * fires, and nothing may carry into the next turn — the condition text says
 * "this turn", and `G.turnEconomy` resets anyway, so a surviving entry would
 * re-evaluate against a fresh economy and could fire for the wrong turn.
 *
 * @param G - The game state, mutated in place.
 */
export function clearDeferredConditionalGrants(G) {
    if (G.deferredConditionalGrants !== undefined) {
        delete G.deferredConditionalGrants;
    }
}
/**
 * Re-checks every deferred grant and fires those whose gate is now satisfied.
 *
 * Iterates in INSERTION ORDER over a snapshot, so the sequence is deterministic
 * and stable across identical runs. An entry that fires is REMOVED, which is what
 * makes the grant idempotent: a threshold crossed, dropped and re-crossed within
 * one turn grants exactly once.
 *
 * @param G - The game state, mutated in place.
 * @param canFire - Predicate: does this entry's hook now pass all its conditions?
 * @param fire - Callback that applies the entry's hook effects.
 */
export function resolveDeferredConditionalGrants(G, canFire, fire) {
    const entries = G.deferredConditionalGrants;
    if (entries === undefined || entries.length === 0) {
        return;
    }
    // why: snapshot before iterating — `fire` mutates G (and may append log lines),
    // and removing from the live array mid-walk would skip entries. Explicit
    // for...of over the snapshot; no .reduce() (.claude/rules/code-style.md).
    const snapshot = [...entries];
    const survivors = [];
    const readyToFire = [];
    for (const entry of snapshot) {
        if (canFire(entry)) {
            readyToFire.push(entry);
        }
        else {
            survivors.push(entry);
        }
    }
    // why: rewrite the list BEFORE firing, so a fire callback that itself defers a
    // new grant (a chained threshold) appends to the surviving list rather than
    // being wiped by this assignment.
    if (survivors.length === 0) {
        delete G.deferredConditionalGrants;
    }
    else {
        G.deferredConditionalGrants = survivors;
    }
    for (const entry of readyToFire) {
        fire(entry);
    }
}
//# sourceMappingURL=deferredConditionalGrants.js.map